import type { RangePeriod } from "@finances/shared";
import { Select } from "./Select.js";

export type PeriodValue = { month: string } | { period: RangePeriod };

interface PeriodPickerProps {
  /** Months with activity, newest first. */
  months: string[];
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  format: (month: string) => string;
  label?: string;
}

const RANGES: Array<{ value: RangePeriod; label: string }> = [
  { value: "last-3-months", label: "Last 3 months" },
  { value: "last-6-months", label: "Last 6 months" },
  { value: "last-12-months", label: "Last 12 months" },
  { value: "all-time", label: "All time" },
];

/**
 * A month, or one of a handful of ranges — "all time" and "the last few
 * months" are real answers to "how much have we spent", not just a wider
 * single month, so they are offered as their own choices rather than made to
 * masquerade as one.
 */
export function PeriodPicker({
  months,
  value,
  onChange,
  format,
  label = "Period",
}: PeriodPickerProps) {
  const selected = "month" in value ? value.month : value.period;
  const options =
    "month" in value && !months.includes(value.month) ? [value.month, ...months] : months;

  return (
    <Select
      aria-label={label}
      value={selected}
      onChange={(event) => {
        const next = event.target.value;
        const range = RANGES.find((option) => option.value === next);
        onChange(range ? { period: range.value } : { month: next });
      }}
    >
      {options.map((month) => (
        <option key={month} value={month}>
          {format(month)}
        </option>
      ))}
      <optgroup label="Ranges">
        {RANGES.map((range) => (
          <option key={range.value} value={range.value}>
            {range.label}
          </option>
        ))}
      </optgroup>
    </Select>
  );
}
